<template>
  <div class="carousel-deck-row">
    <div class="header-row">
      <dashboard-widget-header
        :title="title"
      />
      <router-link
        :to="getAllRoute()"
        class="view-all"
      >
        <span>
          See All
        </span>
      </router-link>
      <div v-if="isScrollNeeded" class="scroll-arrows">
        <div
          class="scroll-arrow-container"
          :class="{ 'arrow-disabled': rowIsAtStart }"
          @click="scrollLeft"
        >
          <icon
            name="scroll-arrow-left"
            :class="{ 'arrow-disabled': rowIsAtStart }"
          />
        </div>
        <div
          class="scroll-arrow-container"
          :class="{ 'arrow-disabled': rowIsAtEnd }"
          @click="scrollRight"
        >
          <icon
            name="scroll-arrow-right"
            :class="{ 'arrow-disabled': rowIsAtEnd }"
          />
        </div>
      </div>
    </div>
    <div :ref="`row-wrapper-${deck.id}`" class="row-wrapper">
      <carousel-deck-card
        v-for="card in cards"
        :key="card.id"
        :card="card"
        :deck="deck"
        :fallback-icon-attachment-id="fallbackIconAttachmentId"
        :fallback-image-type="fallbackImageType"
      />
    </div>
  </div>
</template>

<script>
import Icon from '@components/singletons/icon';
import DashboardWidgetHeader from '@components/dashboard-widgets/header.vue';
import CarouselDeckCard from '@components/dashboard-widgets/CarouselDeckCard.vue';
import { smoothScroll } from '@/utils/scrolling';

export default {
  components: {
    CarouselDeckCard,
    DashboardWidgetHeader,
    Icon,
  },
  props: {
    deck: {
      type: Object,
      required: true,
    },
    fallbackIconAttachmentId: {
      type: Number,
    },
    fallbackImageType: {
      type: String,
    },
  },
  data() {
    return {
      rowWrapperWidth: 0,
      rowWrapperScrollWidth: 0,
      scrollPosition: 0,
    };
  },
  computed: {
    cards() {
      return [...this.deck.cards].sort((a, b) => a.order - b.order);
    },
    cardWidth() {
      return this.fallbackImageType === 'landscape' ? 367 : 170;
    },
    isScrollNeeded() {
      const totalCardWidth = this.cards.length * this.cardWidth + (this.cards.length - 1) * 30;
      return totalCardWidth > this.rowWrapperWidth;
    },
    rowIsAtStart() {
      return this.scrollPosition === 0;
    },
    rowIsAtEnd() {
      return this.scrollPosition === this.rowWrapperScrollWidth - this.rowWrapperWidth;
    },
    title() {
      return this.deck.title;
    },
  },
  mounted() {
    this.updateRowWidth();
    window.addEventListener('resize', this.updateRowWidth);
    this.scrollPosition = 0;
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateRowWidth);
  },
  methods: {
    getAllRoute() {
      return `/decks/${this.deck.id}`;
    },
    scrollLeft() {
      const domRowWrapper = this.$refs[`row-wrapper-${this.deck.id}`];
      let newScrollPosition = Math.max(0, domRowWrapper.scrollLeft - domRowWrapper.offsetWidth);

      if (newScrollPosition < 50) {
        newScrollPosition = 0;
      }
      smoothScroll(domRowWrapper, newScrollPosition);
      this.scrollPosition = newScrollPosition;
    },
    scrollRight() {
      const domRowWrapper = this.$refs[`row-wrapper-${this.deck.id}`];
      let newScrollPosition = Math.min(
        domRowWrapper.scrollWidth - domRowWrapper.offsetWidth,
        domRowWrapper.scrollLeft + domRowWrapper.offsetWidth,
      );

      if (newScrollPosition > domRowWrapper.scrollWidth - domRowWrapper.offsetWidth - 50) {
        newScrollPosition = domRowWrapper.scrollWidth - domRowWrapper.offsetWidth;
      }
      smoothScroll(domRowWrapper, newScrollPosition);
      this.scrollPosition = newScrollPosition;
    },
    updateRowWidth() {
      const rowWrapperElement = this.$refs[`row-wrapper-${this.deck.id}`];
      if (rowWrapperElement) {
        this.rowWrapperWidth = rowWrapperElement.offsetWidth;
        this.rowWrapperScrollWidth = rowWrapperElement.scrollWidth;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.carousel-deck-row {
  margin-top: rem-calc(56);
}

.row-wrapper {
  scrollbar-width: none; /* For Firefox */
  -ms-overflow-style: none; /* For Internet Explorer and Edge */
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: rem-calc(30);
  width: rem-calc(772);
  overflow-x: auto;
  overflow-y: hidden;

  @include breakpoint(medium) {
    width: auto;
  }
}

.row-wrapper::-webkit-scrollbar {
  display: none; /* For Chrome, Safari, and Opera */
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: rem-calc(16);
  margin-bottom: rem-calc(2);
}

.view-all {
  font-size: rem-calc(16);
  line-height: rem-calc(24);
  font-weight: 600;
  color: #326FF0;
  cursor: pointer;
}

.scroll-arrows {
  display: flex;
  gap: rem-calc(8);
}

.scroll-arrow-container {
  cursor: pointer;
  line-height: rem-calc(24);
  text-align: center;
  background-color: #EAECF0;
  border-radius: 2rem;
  height: rem-calc(24);
  width: rem-calc(24);
}

.arrow-disabled {
  cursor: unset;

  img {
    opacity: 0.4;
  }
}
</style>
